import { pmt, roundToDecimal } from "../utils/math";

export interface AmortizationPeriod {
  period: number; // Month index
  payment: number;
  principal: number;
  interest: number;
  balance: number;
}

export interface MortgageResult {
  monthlyPayment: number;
  totalPayments: number;
  totalInterest: number;
  schedule: AmortizationPeriod[];
}

export interface AffordabilityResult {
  maxLoanAmount: number;
  maxHomePrice: number;
  monthlyPayment: number;
  dtiRatio: number;
  isQualified: boolean;
}

export interface RefinanceResult {
  currentMonthlyPayment: number;
  newMonthlyPayment: number;
  monthlySavings: number;
  breakEvenMonths: number;
  totalSavings: number;
}

/**
 * Calculates standard mortgage payments and amortization schedule
 */
export function calculateMortgage(
  principal: number,
  annualRate: number,
  termYears: number,
  frequency: "monthly" | "biweekly" | "weekly" = "monthly"
): MortgageResult {
  let periodsPerYear = 12;
  if (frequency === "biweekly") periodsPerYear = 26;
  if (frequency === "weekly") periodsPerYear = 52;

  const totalPeriods = termYears * periodsPerYear;
  const periodicRate = annualRate / 100 / periodsPerYear;

  // Monthly payment (expressed positive)
  const periodicPayment = Math.abs(pmt(periodicRate, totalPeriods, principal));
  
  let balance = principal;
  const schedule: AmortizationPeriod[] = [];
  let totalInterest = 0;

  for (let i = 1; i <= totalPeriods; i++) {
    const interest = balance * periodicRate;
    const principalPaid = Math.min(balance, periodicPayment - interest);
    balance = Math.max(0, balance - principalPaid);
    totalInterest += interest;

    schedule.push({
      period: i,
      payment: periodicPayment,
      principal: roundToDecimal(principalPaid, 2),
      interest: roundToDecimal(interest, 2),
      balance: roundToDecimal(balance, 2),
    });

    if (balance <= 0) break;
  }

  const totalPayments = periodicPayment * totalPeriods;

  return {
    monthlyPayment: roundToDecimal(periodicPayment, 2),
    totalPayments: roundToDecimal(totalPayments, 2),
    totalInterest: roundToDecimal(totalInterest, 2),
    schedule,
  };
}

/**
 * Calculates loan affordability based on DTI (Debt-to-Income) ratio limits (typically 36%-43%)
 */
export function calculateAffordability(
  annualIncome: number,
  monthlyDebts: number,
  annualRate: number,
  termYears: number,
  downPayment: number,
  dtiLimitPercent = 36
): AffordabilityResult {
  const monthlyGrossIncome = annualIncome / 12;
  const maxAllowedMonthlyDebt = monthlyGrossIncome * (dtiLimitPercent / 100);
  
  // Maximum housing payment is DTI limit minus current monthly debts
  const maxHousingPayment = Math.max(0, maxAllowedMonthlyDebt - monthlyDebts);
  
  const periodicRate = annualRate / 100 / 12;
  const totalPeriods = termYears * 12;

  let maxLoanAmount = 0;
  if (periodicRate > 0) {
    const pvif = Math.pow(1 + periodicRate, totalPeriods);
    maxLoanAmount = (maxHousingPayment * (pvif - 1)) / (periodicRate * pvif);
  } else {
    maxLoanAmount = maxHousingPayment * totalPeriods;
  }

  const maxHomePrice = maxLoanAmount + downPayment;
  const currentDti = grossDtiRatio(monthlyDebts + maxHousingPayment, monthlyGrossIncome);

  return {
    maxLoanAmount: roundToDecimal(maxLoanAmount, 2),
    maxHomePrice: roundToDecimal(maxHomePrice, 2),
    monthlyPayment: roundToDecimal(maxHousingPayment, 2),
    dtiRatio: roundToDecimal(currentDti, 2),
    isQualified: currentDti <= dtiLimitPercent,
  };
}

function grossDtiRatio(totalDebts: number, grossIncome: number): number {
  if (grossIncome <= 0) return 0;
  return (totalDebts / grossIncome) * 100;
}

/**
 * Calculates mortgage savings and payoff duration with extra payments
 */
export function calculateOverpayment(
  principal: number,
  annualRate: number,
  termYears: number,
  extraMonthlyPayment: number
): { originalTermMonths: number; newTermMonths: number; monthsSaved: number; interestSaved: number } {
  const baseResult = calculateMortgage(principal, annualRate, termYears, "monthly");
  const periodicRate = annualRate / 100 / 12;
  const normalPayment = baseResult.monthlyPayment;
  const totalPayment = normalPayment + extraMonthlyPayment;

  let balance = principal;
  let newTermMonths = 0;
  let totalInterestWithOverpayment = 0;

  while (balance > 0 && newTermMonths < termYears * 12 * 2) {
    newTermMonths++;
    const interest = balance * periodicRate;
    const principalPaid = Math.min(balance, totalPayment - interest);
    balance = Math.max(0, balance - principalPaid);
    totalInterestWithOverpayment += interest;
  }

  const interestSaved = baseResult.totalInterest - totalInterestWithOverpayment;
  const monthsSaved = termYears * 12 - newTermMonths;

  return {
    originalTermMonths: termYears * 12,
    newTermMonths,
    monthsSaved: Math.max(0, monthsSaved),
    interestSaved: roundToDecimal(Math.max(0, interestSaved), 2),
  };
}

/**
 * Calculates mortgage refinancing benefit
 */
export function calculateRefinance(
  currentBalance: number,
  currentRate: number,
  currentTermYearsRemaining: number,
  newRate: number,
  newTermYears: number,
  closingCosts: number
): RefinanceResult {
  const currentResult = calculateMortgage(currentBalance, currentRate, currentTermYearsRemaining, "monthly");
  const newResult = calculateMortgage(currentBalance, newRate, newTermYears, "monthly");

  const currentMonthlyPayment = currentResult.monthlyPayment;
  const newMonthlyPayment = newResult.monthlyPayment;
  const monthlySavings = currentMonthlyPayment - newMonthlyPayment;
  
  // Break-even in months = closingCosts / monthlySavings
  const breakEvenMonths = monthlySavings > 0 ? closingCosts / monthlySavings : Infinity;
  const totalSavings = (currentResult.totalInterest) - (newResult.totalInterest + closingCosts);

  return {
    currentMonthlyPayment: roundToDecimal(currentMonthlyPayment, 2),
    newMonthlyPayment: roundToDecimal(newMonthlyPayment, 2),
    monthlySavings: roundToDecimal(monthlySavings, 2),
    breakEvenMonths: breakEvenMonths !== Infinity ? roundToDecimal(breakEvenMonths, 1) : -1,
    totalSavings: roundToDecimal(totalSavings, 2),
  };
}

export interface Debt {
  name: string;
  balance: number;
  rate: number;
  minPayment: number;
}

export interface DebtPayoffPeriod {
  month: number;
  debtsPaidThisMonth: Array<{ name: string; balancePaid: number; remainingBalance: number }>;
  totalPayment: number;
}

/**
 * Calculates debt snowball or avalanche payoff strategy
 */
export function calculateDebtPayoff(
  debts: Debt[],
  strategy: "snowball" | "avalanche",
  extraBudget: number
): { totalInterestPaid: number; monthsToPayoff: number; payoffSchedule: DebtPayoffPeriod[] } {
  // Sort debts based on strategy
  // Snowball: Smallest balance first
  // Avalanche: Highest interest rate first
  const sortedDebts = [...debts].sort((a, b) => {
    if (strategy === "snowball") {
      return a.balance - b.balance;
    } else {
      return b.rate - a.rate;
    }
  });

  const activeBalances = sortedDebts.map((d) => ({ ...d, currentBalance: d.balance }));
  const payoffSchedule: DebtPayoffPeriod[] = [];
  let month = 0;
  let totalInterestPaid = 0;

  // Continue paying until all debts are paid
  while (activeBalances.some((d) => d.currentBalance > 0) && month < 360) {
    month++;
    let monthlyRolloverExtra = extraBudget;
    const debtsPaidThisMonth: DebtPayoffPeriod["debtsPaidThisMonth"] = [];
    
    // First pass: Make minimum payments on all active debts
    for (let i = 0; i < activeBalances.length; i++) {
      const debt = activeBalances[i];
      if (debt.currentBalance <= 0) continue;

      const monthlyRate = debt.rate / 100 / 12;
      const interest = debt.currentBalance * monthlyRate;
      totalInterestPaid += interest;
      
      // Update balance with interest
      debt.currentBalance += interest;

      const payment = Math.min(debt.currentBalance, debt.minPayment);
      debt.currentBalance -= payment;

      debtsPaidThisMonth.push({
        name: debt.name,
        balancePaid: payment,
        remainingBalance: roundToDecimal(debt.currentBalance, 2),
      });

      // If debt is fully paid off, add the remainder of minPayment to rollover budget!
      if (debt.currentBalance <= 0) {
        monthlyRolloverExtra += (debt.minPayment - payment);
      }
    }

    // Second pass: Apply rollover extra payments to the target debt
    if (monthlyRolloverExtra > 0) {
      for (let i = 0; i < activeBalances.length; i++) {
        const debt = activeBalances[i];
        if (debt.currentBalance <= 0) continue;

        const extraPayment = Math.min(debt.currentBalance, monthlyRolloverExtra);
        debt.currentBalance -= extraPayment;
        monthlyRolloverExtra -= extraPayment;

        // Update payoff record for this debt
        const record = debtsPaidThisMonth.find((r) => r.name === debt.name);
        if (record) {
          record.balancePaid += extraPayment;
          record.remainingBalance = roundToDecimal(debt.currentBalance, 2);
        }

        if (monthlyRolloverExtra <= 0) break;
      }
    }

    payoffSchedule.push({
      month,
      debtsPaidThisMonth,
      totalPayment: debtsPaidThisMonth.reduce((sum, d) => sum + d.balancePaid, 0),
    });
  }

  return {
    totalInterestPaid: roundToDecimal(totalInterestPaid, 2),
    monthsToPayoff: month,
    payoffSchedule,
  };
}

/**
 * Calculates rent vs buy comparison
 */
export function calculateRentVsBuy(
  homePrice: number,
  downPayment: number,
  mortgageRate: number,
  termYears: number,
  monthlyRent: number,
  yearsToCompare: number,
  appreciationRate: number = 3,
  maintenanceCostPercent: number = 1
): { totalCostBuying: number; totalCostRenting: number; netCostDifference: number } {
  const loanAmount = homePrice - downPayment;
  const mortgageResult = calculateMortgage(loanAmount, mortgageRate, termYears);
  
  const monthsToCompare = yearsToCompare * 12;
  const monthlyMaintenanceCost = (homePrice * (maintenanceCostPercent / 100)) / 12;
  
  // Buying costs
  const totalMortgagePayments = mortgageResult.monthlyPayment * Math.min(monthsToCompare, termYears * 12);
  const totalMaintenanceCosts = monthlyMaintenanceCost * monthsToCompare;
  
  // Calculate future home value with overflow protection
  const growthFactor = Math.pow(1 + appreciationRate / 100, yearsToCompare);
  const futureHomeValue = Number.isFinite(growthFactor) && growthFactor < Number.MAX_SAFE_INTEGER / homePrice 
    ? homePrice * growthFactor 
    : homePrice; // Fallback to no appreciation if overflow would occur
  
  const totalCostBuying = downPayment + totalMortgagePayments + totalMaintenanceCosts - futureHomeValue;
  
  // Renting costs
  const totalCostRenting = monthlyRent * monthsToCompare;
  
  const netCostDifference = totalCostRenting - totalCostBuying;
  
  return {
    totalCostBuying: roundToDecimal(totalCostBuying, 2),
    totalCostRenting: roundToDecimal(totalCostRenting, 2),
    netCostDifference: roundToDecimal(netCostDifference, 2),
  };
}

/**
 * Calculates auto/car loan payments (alias for mortgage calculator)
 */
export function calculateAutoLoan(
  principal: number,
  annualRate: number,
  termYears: number
): MortgageResult {
  return calculateMortgage(principal, annualRate, termYears, "monthly");
}

/**
 * US Student Loan SAVE Transition & Payment Shock Calculator (2026)
 * Official Sourcing: U.S. Department of Education, Federal Student Aid (July 1, 2026 90-day window).
 * Compares terminated SAVE (~5% discretionary income cap) vs. new 10% IDR / RAP plans.
/**
 * US Student Loan SAVE Transition & Repayment Assistance Plan (RAP) Engine (July 2026)
 * Federal Poverty Level (FPL 2026 Contiguous US): $15,960 baseline for 1-person + $5,500 per additional member.
 * RAP (July 2026): Straight AGI percentage scale (1%-10%) with $50/mo dependent credit & $10/mo mandatory min payment.
 */
export function calculateStudentLoanSaveTransition(
  agi: number = 65000,
  familySize: number = 1,
  loanBalance: number = 40000,
  interestRate: number = 6.0,
  loanType: "direct_sub_unsub" | "direct_plus_grad" | "parent_plus" = "direct_sub_unsub",
  isPublicService: "yes" | "no" = "no"
) {
  const basePovertyLine = 15960 + Math.max(0, (familySize || 1) - 1) * 5500;
  
  // 1. Terminated SAVE Plan Math (225% poverty line, 5% undergrad discretionary cap)
  const savePovertyThreshold = 2.25 * basePovertyLine;
  const saveDiscretionaryIncome = Math.max(0, (agi || 0) - savePovertyThreshold);
  const oldSaveMonthlyPayment = Math.max(0, (saveDiscretionaryIncome * 0.05) / 12);

  // 2. Legacy IBR / 10% IDR Plan Math (150% poverty line, 10% discretionary cap)
  const legacyPovertyThreshold = 1.50 * basePovertyLine;
  const legacyDiscretionaryIncome = Math.max(0, (agi || 0) - legacyPovertyThreshold);
  let newIdrMonthlyPayment = Math.max(0, (legacyDiscretionaryIncome * 0.10) / 12);

  // 3. New Repayment Assistance Plan (RAP - July 1, 2026)
  // RAP uses straight AGI % (1%-10%), $50/mo per dependent credit, $10/mo minimum.
  let rapMonthlyPayment = 0;
  let rapEligibilityStatus = "Eligible for RAP (30-Year Balance Cancellation)";
  let planName = "RAP (Repayment Assistance Plan)";

  if (loanType === "parent_plus") {
    const icrPovertyThreshold = 1.0 * basePovertyLine;
    const icrDiscretionaryIncome = Math.max(0, (agi || 0) - icrPovertyThreshold);
    newIdrMonthlyPayment = Math.max(0, (icrDiscretionaryIncome * 0.20) / 12);
    rapMonthlyPayment = 0;
    rapEligibilityStatus = "Ineligible for RAP (Parent PLUS Restricted to ICR/Standard)";
    planName = "ICR (Parent PLUS Restricted)";
  } else {
    let agiRate = 0.10;
    if (agi <= 25000) agiRate = 0.01;
    else if (agi <= 35000) agiRate = 0.02;
    else if (agi <= 40000) agiRate = 0.03;
    else if (agi <= 50000) agiRate = 0.04;
    else if (agi <= 60000) agiRate = 0.05;
    else if (agi <= 75000) agiRate = 0.06;
    else if (agi <= 90000) agiRate = 0.07;
    else if (agi <= 110000) agiRate = 0.08;
    else if (agi <= 130000) agiRate = 0.09;

    const baseRapAnnual = agi * agiRate;
    const dependentCreditMonthly = Math.max(0, (familySize || 1) - 1) * 50;
    const rawRapMonthly = Math.max(10, (baseRapAnnual / 12) - dependentCreditMonthly);
    rapMonthlyPayment = Math.max(10, rawRapMonthly);
    planName = `RAP (${Math.round(agiRate * 100)}% AGI Rate)`;
  }

  // 4. Standard 10-Year Repayment Amortization
  const monthlyRate = (interestRate || 6.0) / 100 / 12;
  const numPayments = 120;
  let standard10YrMonthlyPayment = 0;
  if (monthlyRate > 0 && loanBalance > 0) {
    standard10YrMonthlyPayment = (loanBalance * (monthlyRate * Math.pow(1 + monthlyRate, numPayments))) / (Math.pow(1 + monthlyRate, numPayments) - 1);
  } else {
    standard10YrMonthlyPayment = loanBalance / numPayments;
  }

  // 5. Payment Shock Math
  const effectiveNewPayment = loanType === "parent_plus" ? newIdrMonthlyPayment : rapMonthlyPayment;
  const paymentShockDollar = Math.max(0, effectiveNewPayment - oldSaveMonthlyPayment);
  const paymentShockPercent = oldSaveMonthlyPayment > 0
    ? ((effectiveNewPayment - oldSaveMonthlyPayment) / oldSaveMonthlyPayment) * 100
    : 100;

  // 6. Recommended Action
  let recommendation = "Select RAP or Legacy IBR before the 90-day window closes. Missing the window auto-enrolls you into Standard Plan.";
  if (isPublicService === "yes") {
    recommendation = "Enroll in RAP or Legacy IBR immediately to keep earning PSLF 120-payment forgiveness credits.";
  } else if (loanType === "parent_plus") {
    recommendation = "Parent PLUS loans cannot use RAP; evaluate ICR vs 10-Year Standard Plan.";
  } else if (standard10YrMonthlyPayment < rapMonthlyPayment) {
    recommendation = "Standard 10-Year plan offers lower monthly payment than RAP based on your income.";
  }

  return {
    oldSaveMonthlyPayment: Number(oldSaveMonthlyPayment.toFixed(2)),
    newIdrMonthlyPayment: Number(newIdrMonthlyPayment.toFixed(2)),
    rapMonthlyPayment: Number(rapMonthlyPayment.toFixed(2)),
    rapEligibilityStatus,
    standard10YrMonthlyPayment: Number(standard10YrMonthlyPayment.toFixed(2)),
    paymentShockDollar: Number(paymentShockDollar.toFixed(2)),
    paymentShockPercent: Number(paymentShockPercent.toFixed(1)),
    pslfStatus: isPublicService === "yes" ? "PSLF Eligible (120 Payments to 100% Tax-Free Forgiveness)" : "RAP Forgiveness (30 Years of Qualifying Payments Required)",
    recommendedPlan: planName,
    recommendationNote: recommendation
  };
}

/**
 * India RBI Anti-Mis-Selling & Forced Bundling Compensation Checker (2026)
 * Sourced from RBI Commercial Banks Responsible Business Conduct Second Amendment Directions
 * and RBI Integrated Ombudsman Scheme (RB-IOS 2026 - Effective 1 July 2026).
 * Max compensation limit: ₹30 Lakh under RB-IOS 2026.
 */
export function calculateRbiMisSellingCompensation(
  loanAmount: number = 2500000,
  forcedInsurancePremium: number = 150000,
  hiddenProcessingFees: number = 25000,
  monthsElapsed: number = 12,
  saleDatePeriod: "post_july_2026" | "pre_july_2026" = "post_july_2026"
) {
  const baseMisSoldAmount = (forcedInsurancePremium || 0) + (hiddenProcessingFees || 0);

  // RBI Ombudsman Penalty Interest Rate (9.0% p.a. simple interest calculation)
  const annualInterestRate = 0.09;
  const elapsedYears = (monthsElapsed || 12) / 12;
  const accruedInterestPenalty = baseMisSoldAmount * annualInterestRate * elapsedYears;

  // Capped at ₹30 Lakh statutory limit under RB-IOS 2026
  const totalClaimAmount = Math.min(3000000, Math.round(baseMisSoldAmount + accruedInterestPenalty));

  let liabilityNote = "⚠️ DRAFT RULE: Proposed RBI Responsible Business Conduct (Amendment) Directions 2026 (Draft/Proposed - Expected July 1, 2026).";
  if (saleDatePeriod === "pre_july_2026") {
    liabilityNote = "Pre-July 2026 Sale: Recourse available via standard Consumer Court & Banking Ombudsman; subjective burden of proof applies.";
  }

  const forcedInsuranceRatio = (loanAmount || 0) > 0
    ? Number(((forcedInsurancePremium / loanAmount) * 100).toFixed(2))
    : 0;

  return {
    loanAmount: Math.round(loanAmount),
    baseMisSoldAmount: Math.round(baseMisSoldAmount),
    accruedInterestPenalty: Math.round(accruedInterestPenalty),
    totalClaimAmount: Math.round(totalClaimAmount),
    forcedInsuranceRatio,
    liabilityNote,
    ombudsmanCapStatus: totalClaimAmount >= 3000000 ? "Capped at ₹30 Lakh Statutory Maximum" : "Within ₹30 Lakh Statutory Claim Limit"
  };
}

/**
 * Canada OSFI Mortgage Stress Test "Straight Switch" Exemption Checker (2026)
 * Sourced from OSFI.gc.ca (B-20 / B-21 Guidelines).
 * Straight Switch Exemption: Transferring uninsured/insured mortgage to a new lender without increasing loan amount or extending amortization.
 * Stress test qualifying rate: Max(5.25%, Contract Rate + 2.0%).
 * Portfolio LTI cap: 4.5x loan-to-income (evaluated at lender portfolio level).
 */
export function calculateOsfiMortgageSwitchExemption(
  mortgageBalance: number = 450000,
  propertyValue: number = 650000,
  contractRate: number = 4.85,
  additionalCashOut: number = 0,
  isAmortizationExtended: "no" | "yes" = "no",
  annualHouseholdIncome: number = 110000
) {
  const isStraightSwitch = (additionalCashOut || 0) <= 0 && isAmortizationExtended === "no";
  const totalMortgageAmount = (mortgageBalance || 0) + Math.max(0, additionalCashOut || 0);

  // Qualifying Rate:
  // Straight Switch -> Exempt from Stress Test! Use contractRate directly.
  // Cash-Out / Extended -> Full Stress Test Applies! Max(5.25%, contractRate + 2.0%).
  const stressTestRate = Math.max(5.25, (contractRate || 4.85) + 2.0);
  const effectiveQualifyingRate = isStraightSwitch ? (contractRate || 4.85) : stressTestRate;

  // Monthly Amortization Payment (25-year standard term)
  const computeMonthlyPayment = (principal: number, annualRate: number, years: number = 25) => {
    const r = annualRate / 100 / 12;
    const n = years * 12;
    if (r <= 0) return principal / n;
    return (principal * (r * Math.pow(1 + r, n))) / (Math.pow(1 + r, n) - 1);
  };

  const actualMonthlyPayment = Math.round(computeMonthlyPayment(totalMortgageAmount, contractRate || 4.85));
  const stressTestMonthlyPayment = Math.round(computeMonthlyPayment(totalMortgageAmount, stressTestRate));

  const ltvRatio = (propertyValue || 0) > 0
    ? Number(((totalMortgageAmount / propertyValue) * 100).toFixed(2))
    : 0;

  const ltiRatio = (annualHouseholdIncome || 0) > 0
    ? Number((totalMortgageAmount / annualHouseholdIncome).toFixed(2))
    : 0;

  let exemptionStatus = "EXEMPT from OSFI Stress Test — Qualify at Contract Rate (" + (contractRate || 4.85) + "%)";
  let exemptionDetails = "Your mortgage transfer is a Straight Switch (no loan increase or extended amortization). You can shop for competitive lender rates without stress testing.";

  if (!isStraightSwitch) {
    exemptionStatus = "NOT EXEMPT — Full Stress Test Required (" + stressTestRate.toFixed(2) + "%)";
    exemptionDetails = "Refinancing with additional cash-out or extending your amortization triggers the full OSFI Stress Test qualifying rate (Contract Rate + 2.0%).";
  }

  const ltiAlert = ltiRatio > 4.5
    ? "High Loan-to-Income (LTI " + ltiRatio + "x > 4.5x OSFI portfolio threshold) — Subject to lender portfolio caps."
    : "Standard Loan-to-Income (LTI " + ltiRatio + "x ≤ 4.5x threshold).";

  const minIncomeExemptSwitch = Math.round((actualMonthlyPayment * 12) / 0.32);
  const minIncomeFullStressTest = Math.round((stressTestMonthlyPayment * 12) / 0.32);

  return {
    totalMortgageAmount: Math.round(totalMortgageAmount),
    contractRate: contractRate || 4.85,
    stressTestRate: Number(stressTestRate.toFixed(2)),
    effectiveQualifyingRate: Number(effectiveQualifyingRate.toFixed(2)),
    actualMonthlyPayment,
    stressTestMonthlyPayment,
    qualifyingMonthlyPaymentExempt: actualMonthlyPayment,
    qualifyingMonthlyPaymentFull: stressTestMonthlyPayment,
    minIncomeExemptSwitch,
    minIncomeFullStressTest,
    ltvRatio,
    ltiRatio,
    isStraightSwitch,
    exemptionStatus,
    exemptionDetails,
    ltiAlert
  };
}

/**
 * NZ CCCFA complaint route and documented-charge tally.
 * The FMA became the current CCCFA regulator for old and new loans on
 * 1 July 2026; active Commerce Commission investigations also transferred.
 * A complaint outcome cannot be calculated as a percentage of loan principal.
 * Sources:
 * https://www.comcom.govt.nz/business/transfer-of-responsibility-for-regulation-of-consumer-credit/
 * https://www.fma.govt.nz/consumer/everyday-finance/disputes-and-consumer-protection
 */
export function calculateNzFmaCccfaEligibility(
  loanOriginationDate: "post_july_2026" | "pre_july_2026" = "post_july_2026",
  lenderType: "registered_bank" | "nbfc_finance_company" | "unregulated_p2p" = "registered_bank",
  issueType: "unreasonable_fees" | "predatory_irresponsible_lending" | "harassment_collection" | "lender_licensing_failure" = "predatory_irresponsible_lending",
  interestPaid: number = 4000,
  feesPaid: number = 500,
  hasFmaLicense: "yes" | "no" = "yes"
) {
  const safeInterest = Number.isFinite(interestPaid) ? Math.max(0, Math.min(1_000_000_000, interestPaid)) : 0;
  const safeFees = Number.isFinite(feesPaid) ? Math.max(0, Math.min(1_000_000_000, feesPaid)) : 0;
  const regulatorName = "Financial Markets Authority (FMA) — current CCCFA regulator";
  const licensingRegime = lenderType === "unregulated_p2p"
    ? "A one-off private loan may be outside consumer-credit licensing; business lending may require an FMA consumer credit provider licence. Check the contract and FSPR."
    : "FMA consumer credit provider licensing applies; some pre-July certifications or existing bank/FMA licences were deemed licences at transfer. Verify the lender on the FSPR.";
  const licensingAlert = hasFmaLicense === "no"
    ? "You have not verified a consumer credit provider licence. Check the FSPR or ask the FMA; absence of a number on paperwork alone does not prove unlawful lending."
    : "You reported a verified licence. Confirm the lender's legal name and current FSPR entry before relying on that status.";
  const issueNotice = issueType === "predatory_irresponsible_lending"
    ? "Gather the lender's affordability assessment, income and expense evidence, contract, statements and payment history. A breach and remedy require individual review."
    : issueType === "unreasonable_fees"
      ? "Compare each charged fee with the contract disclosure and ask the lender to explain its basis. A refund depends on which fee is unlawful and what you actually paid."
      : issueType === "harassment_collection"
        ? "Keep dated collection messages, call records and any repossession notices. The FMA and dispute scheme can assess the conduct."
        : "Check the lender's legal name and FSPR licence status. Licensing depends on the activity and any exemption; do not infer an offence from missing paperwork alone.";
  const contractDateNotice = loanOriginationDate === "pre_july_2026"
    ? "The loan predates 1 July 2026, but current CCCFA regulatory reports still go to the FMA. Older case records may have transferred from the Commerce Commission."
    : "The loan began on or after 1 July 2026. Current CCCFA regulatory reports go to the FMA.";
  const escalationRoute = "Complain to the lender in writing; if unresolved, contact its approved dispute resolution scheme. New Zealand has four: Banking Ombudsman, IFSO, FSCL and FDRS. Check the FSPR for the lender's scheme. You may also report a wider conduct concern to the FMA, which does not award individual compensation.";
  const documentedInterestPaid = Math.round(safeInterest * 100) / 100;
  const documentedFeesPaid = Math.round(safeFees * 100) / 100;
  const documentedChargesForReview = Math.round((documentedInterestPaid + documentedFeesPaid) * 100) / 100;

  return {
    regulatorName,
    licensingRegime,
    enforcementPowers: "The FMA may use CCCFA stop and direction orders. A dispute scheme or court assesses any individual remedy; this tool does not estimate an award.",
    escalationRoute,
    licensingAlert,
    issueNotice,
    contractDateNotice,
    documentedInterestPaid,
    documentedFeesPaid,
    documentedChargesForReview,
    regulatoryNotice: "On 1 July 2026 the FMA took over CCCFA oversight, including active older investigations. A loan's origination date does not determine the current regulator."
  };
}

/**
 * Offset Account Error Cost Calculator (Australia, ASIC REP 837, 29 July 2026)
 *
 * Estimates the extra home-loan interest a borrower paid while a mortgage
 * offset account was open but not reducing the loan's interest, the single
 * most common failure ASIC found in REP 837 (55% of bank-identified failures
 * were "offset account opened but not linked"). Simple-interest estimate:
 * banks charge interest daily on a moving balance, so the bank's own
 * remediation number will differ. Source: ASIC media release 26-173MR and
 * Report 837, both published 29 July 2026.
 */
export function calculateOffsetErrorCost(
  loanBalance: number,
  offsetBalance: number,
  interestRate: number,
  monthsUnlinked: number
): {
  extraInterestPaid: number;
  monthlyExtraInterest: number;
  dailyExtraInterest: number;
  effectiveOffsetBalance: number;
} {
  const loan = Math.max(0, loanBalance || 0);
  const offset = Math.max(0, offsetBalance || 0);
  const rate = Math.max(0, interestRate || 0) / 100;
  const months = Math.max(0, monthsUnlinked || 0);

  // An offset can only ever offset up to the loan balance itself.
  const effectiveOffset = Math.min(offset, loan);

  const yearlyExtra = effectiveOffset * rate;
  const extraInterestPaid = yearlyExtra * (months / 12);

  return {
    extraInterestPaid: Math.round(extraInterestPaid),
    monthlyExtraInterest: Math.round(yearlyExtra / 12),
    dailyExtraInterest: Math.round((yearlyExtra / 365) * 100) / 100,
    effectiveOffsetBalance: Math.round(effectiveOffset),
  };
}

/**
 * NZ final-year Fees Free completion-cutoff exposure, September 2026.
 * TEC's final-year policy parameters (updated 4 September 2026), parameters
 * 6 and 12-13: completion by 31 December 2026 and NZ$12,000 including GST
 * ceiling, after the 1 EFTS or 24-month study window is applied. IRD's
 * 17 September 2026 clarification confirms there are no transitional rules
 * for learners already studying who complete from 2027 onward.
 *
 * This is conditional scenario arithmetic. IRD/TEC, not this widget,
 * determines the fee window, residency and individual entitlement.
 * Sources:
 * https://www.tec.govt.nz/assets/Publications-and-others/final-year-fees-free-policy-parameters.pdf
 * https://www.ird.govt.nz/updates/news-folder/2026/fees-free-will-end-without-transitional-arrangements
 */
export function calculateNzFeesFreeCutoff(
  completionTiming: "by_2026_12_31" | "from_2027_01_01" = "by_2026_12_31",
  qualifyingFees: number = 8600,
  otherEligibilityMet: "yes" | "no" = "yes",
  previousFeesFree: "yes" | "no" = "no"
) {
  const safeFees = Number.isFinite(qualifyingFees) ? Math.max(0, qualifyingFees) : 0;
  const withinOtherRules = otherEligibilityMet === "yes" && previousFeesFree === "no";
  const supportIfOnTime = withinOtherRules ? Math.round(Math.min(safeFees, 12000) * 100) / 100 : 0;
  const completedByCutoff = completionTiming === "by_2026_12_31";
  const supportUnderSelectedTiming = completedByCutoff ? supportIfOnTime : 0;
  const amountLostAfterCutoff = completedByCutoff ? 0 : supportIfOnTime;

  const completionStatus = otherEligibilityMet !== "yes"
    ? "Other eligibility is unconfirmed or unmet. This tool cannot determine your entitlement; check myIR and TEC before relying on the amount."
    : previousFeesFree === "yes"
      ? "A previous first-year Fees Free payment generally prevents a second final-year entitlement. Confirm your record with IRD."
      : completedByCutoff
        ? "Completion by 31 December 2026 meets the time cutoff only. IRD and TEC still decide your full eligibility and eligible fees."
        : "Completion from 1 January 2027 misses the final-year Fees Free cutoff, including for people already enrolled. There is no transitional arrangement.";

  const applicationNote = completedByCutoff
    ? "For 2026 completion, apply after passing, within 12 months of your recorded completion date and no later than 31 December 2027."
    : "Finishing in 2027 or later does not create a Fees Free claim. The 2027 application window applies only to eligible 2026 completers.";

  return {
    supportIfOnTime,
    supportUnderSelectedTiming,
    amountLostAfterCutoff,
    completionStatus,
    applicationNote,
  };
}

/**
 * California AB 1847 (Chapter 242, Statutes of 2026) planning arithmetic.
 * Civil Code §3273.23(b),(f) sets a 24-month total, including earlier wildfire
 * forbearance, with an initial period of up to 90 days and requested 90-day
 * extensions. We subtract servicer-confirmed MONTHS; we do not equate 24
 * calendar months to 720 days or infer the approval period from this tool.
 * Civil Code §3273.24 says postponed payments remain owed; end-of-loan
 * deferral is offered unless investor contracts/servicing guidelines forbid it.
 * Source (chaptered 16 Sep 2026):
 * https://leginfo.legislature.ca.gov/faces/billTextClient.xhtml?bill_id=202520260AB1847
 */
export function calculateCaliforniaWildfireForbearance(
  priorForbearanceMonths: number = 10,
  requestedAdditionalMonths: number = 12,
  monthlyMortgagePayment: number = 2500
) {
  const wholeMonths = (value: number) => Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
  const prior = Math.min(24, wholeMonths(priorForbearanceMonths));
  const requested = Math.min(24, wholeMonths(requestedAdditionalMonths));
  const payment = Number.isFinite(monthlyMortgagePayment) ? Math.max(0, Math.min(50_000, monthlyMortgagePayment)) : 0;
  const remainingBeforeRequest = Math.max(0, 24 - prior);
  const monthsWithinCap = Math.min(requested, remainingBeforeRequest);
  const monthsBeyondCap = Math.max(0, requested - monthsWithinCap);
  const remainingAfterRequest = remainingBeforeRequest - monthsWithinCap;
  const illustrativePausedPayments = Math.round(monthsWithinCap * payment * 100) / 100;

  const approvalNotice = remainingBeforeRequest === 0
    ? "The servicer-counted 24-month cap is exhausted in this model. No proposed new month fits. Confirm exact dates and any other relief directly with your servicer."
    : "This is cap arithmetic, not approval. AB 1847 applies to the specified January 2025 LA wildfire disaster. An initial period is up to 90 days; each later 90-day extension requires a separate request. Your servicer must confirm exact dates and eligibility.";
  const documentationNotice = monthsWithinCap === 0
    ? "No proposed new month falls inside the cap in this model, so this tool cannot identify a within-cap extension for documentary review."
    : prior + monthsWithinCap > 12
    ? "A request taking total forbearance past 12 months may trigger extra documentation only under the law's written investor-request conditions. Ask your servicer what it needs and why."
    : "The beyond-12-month documentation condition is not reached in this modeled span, but the servicer may still assess the required hardship and habitability affirmations.";
  const repaymentNotice = "Paused payments are still owed. If you were current when forbearance began, AB 1847 bars a required lump-sum repayment. A servicer must offer end-of-loan deferral unless the applicable investor contract or servicing guidelines prohibit it.";

  return {
    priorCountedMonths: prior,
    remainingBeforeRequest,
    monthsWithinCap,
    monthsBeyondCap,
    remainingAfterRequest,
    illustrativePausedPayments,
    approvalNotice,
    documentationNotice,
    repaymentNotice,
  };
}
